import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Loader2, Copy, CheckCircle2, Clock, Coins } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const CreditsPanel = () => {
  const [credits, setCredits] = useState(0);
  const [wallet, setWallet] = useState("");
  const [packages, setPackages] = useState([]);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const load = async () => {
    try {
      const [c, p] = await Promise.all([api.get("/me/credits"), api.get("/packages")]);
      setCredits(c.data.credits ?? 0);
      setWallet(c.data.wallet_address || "");
      setOrder(c.data.pending || null);
      setPackages(p.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000); // poll for payment confirmation
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (key) => {
    setBuying(key);
    try {
      const { data } = await api.post("/me/payment", { package_key: key });
      setOrder(data);
      setWallet(data.wallet_address || wallet);
      toast.success("Ödeme talebi oluşturuldu. Belirtilen TUTARI tam olarak gönderin.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Talep oluşturulamadı");
    } finally {
      setBuying(null);
    }
  };

  const cancel = async () => {
    try {
      await api.delete("/me/payment");
      setOrder(null);
      toast.success("Ödeme talebi iptal edildi");
    } catch {
      toast.error("İptal edilemedi");
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopyalandı`);
  };

  return (
    <div data-testid="credits-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border-subtle)" }}>
        <Coins className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-bold">Sorgu Kredisi & Lisans</h3>
        <span data-testid="credits-balance" className="ml-auto text-sm font-bold px-3 py-1 rounded-full"
          style={{ background: credits > 0 ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.12)", color: credits > 0 ? "#10B981" : "#FCA5A5" }}>
          {loading ? "…" : `${credits} sorgu hakkı`}
        </span>
      </div>

      <div className="px-5 sm:px-6 py-4 space-y-4">
        {credits <= 0 && !order && (
          <div className="text-sm px-4 py-3 rounded-xl border" style={{ background: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.3)", color: "#FCD34D" }}>
            Sorgu krediniz yok. Grafik analizi yapabilmek için bir paket satın alın.
          </div>
        )}

        {order && order.status === "pending" ? (
          <div data-testid="pending-order" className="rounded-xl border p-4" style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.06)" }}>
            <div className="flex items-center gap-2 mb-3 text-sm font-bold" style={{ color: "#FCD34D" }}>
              <Clock className="w-4 h-4" /> Ödeme Bekleniyor · {order.credits} sorgu
            </div>
            <p className="text-[12px] mb-3" style={{ color: "var(--text-muted)" }}>
              Aşağıdaki cüzdana <b>USDT (BEP20 · BSC ağı)</b> olarak <b>TAM OLARAK</b> şu tutarı gönderin. Tutar size özeldir; doğru eşleşme için kuruşuna kadar aynı olmalı.
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: "var(--bg-main)" }}>
                <div>
                  <div className="text-[9px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Gönderilecek Tutar</div>
                  <div data-testid="pay-amount" className="text-lg font-bold font-mono-term" style={{ color: "#10B981" }}>{order.expected_amount} USDT</div>
                </div>
                <button data-testid="copy-amount" onClick={() => copy(order.expected_amount, "Tutar")} className="p-2 rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}><Copy className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: "var(--bg-main)" }}>
                <div className="min-w-0">
                  <div className="text-[9px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Cüzdan (BEP20)</div>
                  <div data-testid="pay-wallet" className="text-[12px] font-mono-term truncate" style={{ color: "var(--text-primary)" }}>{order.wallet_address || wallet}</div>
                </div>
                <button data-testid="copy-wallet" onClick={() => copy(order.wallet_address || wallet, "Cüzdan")} className="p-2 rounded-lg border shrink-0" style={{ borderColor: "var(--border-subtle)" }}><Copy className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-[11px] mt-3" style={{ color: "var(--text-muted)" }}>
              Ödeme ağ üzerinde onaylandığında krediniz otomatik yüklenir (birkaç dakika sürebilir).
            </p>
            <button data-testid="cancel-order" onClick={cancel} className="mt-3 text-[12px] underline" style={{ color: "#FCA5A5" }}>Talebi iptal et</button>
          </div>
        ) : (
          <>
            {!wallet && (
              <div className="text-sm px-4 py-3 rounded-xl border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>
                Ödeme cüzdanı henüz ayarlanmadı. Lütfen yönetici ile iletişime geçin.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {packages.map((p) => (
                <button
                  key={p.key}
                  data-testid={`buy-package-${p.key}`}
                  onClick={() => buy(p.key)}
                  disabled={buying === p.key || !wallet}
                  className="rounded-xl border p-3 text-center transition-all hover:brightness-110 disabled:opacity-40"
                  style={{ borderColor: "var(--border-subtle)", background: "var(--bg-main)" }}
                >
                  <div className="text-lg font-bold" style={{ color: "#10B981" }}>${p.usd}</div>
                  <div className="text-[12px] font-semibold">{p.credits} sorgu</div>
                  <div className="mt-2 inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg" style={{ background: "rgba(59,130,246,0.15)", color: "#60A5FA" }}>
                    {buying === p.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wallet className="w-3 h-3" />} Satın Al
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
